// Serviço de Notificações PWA
import { getApiUrl } from '../config/environment';
import { BRAND_LOGO_192 } from '../config/brandAssets';

export interface NotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  image?: string;
  data?: any;
  actions?: NotificationAction[];
  tag?: string;
  requireInteraction?: boolean;
  silent?: boolean;
}

export interface NotificationAction {
  action: string;
  title: string;
  icon?: string;
}

export class NotificationService {
  private registration: ServiceWorkerRegistration | null = null;
  private publicVapidKey: string = 'BH-ekukSL-U94z9pRLZYboUvABAXVUErIw4iMPH99L6QpWV8Px6aTx4Zso3fIl1yFmEjoet-vm9OeI-Sg0NaCK4'; // Chave VAPID pública real

  constructor() {
    this.initializeServiceWorker();
  }

  /**
   * Inicializa o Service Worker
   */
  private async initializeServiceWorker(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        this.registration = await navigator.serviceWorker.register('/sw.js');
        console.log('Service Worker registrado com sucesso:', this.registration);
        
        // Aguardar ativação do Service Worker
        await navigator.serviceWorker.ready;
        console.log('Service Worker ativo e pronto');
      } catch (error) {
        console.error('Erro ao registrar Service Worker:', error);
      }
    } else {
      console.warn('Service Worker não suportado neste navegador');
    }
  }

  /**
   * Solicita permissão para notificações
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('Notificações não são suportadas neste navegador');
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      console.log('Permissão de notificação:', permission);
      
      if (permission === 'granted') {
        // Registrar no backend se a permissão foi concedida
        await this.subscribeUser();
      }
      
      return permission;
    } catch (error) {
      console.error('Erro ao solicitar permissão de notificação:', error);
      return 'denied';
    }
  }

  /**
   * Verifica se as notificações estão habilitadas
   */
  isNotificationEnabled(): boolean {
    return 'Notification' in window && Notification.permission === 'granted';
  }

  /**
   * Verifica se as notificações estão disponíveis
   */
  isNotificationSupported(): boolean {
    return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
  }

  /**
   * Inscreve o usuário para notificações push
   */
  private async subscribeUser(): Promise<void> {
    if (!this.registration) {
      console.error('Service Worker não registrado');
      return;
    }

    try {
      // Verificar se já existe uma subscrição
      let subscription = await this.registration.pushManager.getSubscription();
      
      if (!subscription) {
        // Criar nova subscrição
        subscription = await this.registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(this.publicVapidKey)
        });
        console.log('Nova subscrição criada:', subscription);
      }

      // Enviar subscrição para o backend
      if (subscription) {
        await this.sendSubscriptionToBackend(subscription);
      }
    } catch (error) {
      console.error('Erro ao inscrever usuário para notificações push:', error);
    }
  }

  /**
   * Converte chave VAPID para Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Envia subscrição para o backend
   */
  private async sendSubscriptionToBackend(subscription: PushSubscription): Promise<void> {
    try {
      const response = await fetch(`${getApiUrl()}/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          subscription: subscription.toJSON(),
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error(`Erro ao enviar subscrição: ${response.status}`);
      }

      console.log('Subscrição enviada para o backend com sucesso');
    } catch (error) {
      console.error('Erro ao enviar subscrição para o backend:', error);
    }
  }

  /**
   * Exibe uma notificação local
   */
  async showNotification(payload: NotificationPayload): Promise<void> {
    if (!this.isNotificationEnabled()) {
      console.warn('Notificações não estão habilitadas');
      return;
    }

    if (!this.registration) {
      console.error('Service Worker não registrado');
      return;
    }

    try {
      await this.registration.showNotification(payload.title, {
        body: payload.body,
        icon: payload.icon || BRAND_LOGO_192,
        badge: payload.badge || BRAND_LOGO_192,
        image: payload.image,
        data: payload.data,
        actions: payload.actions,
        tag: payload.tag,
        requireInteraction: payload.requireInteraction || false,
        silent: payload.silent || false,
        vibrate: [200, 100, 200]
      });
      
      console.log('Notificação exibida:', payload.title);
    } catch (error) {
      console.error('Erro ao exibir notificação:', error);
    }
  }


  /**
   * Cancela todas as notificações
   */
  async clearAllNotifications(): Promise<void> {
    if (!this.registration) {
      return;
    }

    try {
      const notifications = await this.registration.getNotifications();
      notifications.forEach(notification => notification.close());
      console.log('Todas as notificações foram removidas');
    } catch (error) {
      console.error('Erro ao limpar notificações:', error);
    }
  }

  /**
   * Desinscreve o usuário das notificações push
   */
  async unsubscribeUser(): Promise<void> {
    if (!this.registration) {
      return;
    }

    try {
      const subscription = await this.registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
        console.log('Usuário desinscrito das notificações push');
        
        // Notificar o backend
        await this.removeSubscriptionFromBackend(subscription);
      }
    } catch (error) {
      console.error('Erro ao desinscrever usuário:', error);
    }
  }

  /**
   * Remove subscrição do backend
   */
  private async removeSubscriptionFromBackend(subscription: PushSubscription): Promise<void> {
    try {
      const response = await fetch(`${getApiUrl()}/notifications/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          subscription: subscription.toJSON()
        })
      });

      if (!response.ok) {
        throw new Error(`Erro ao remover subscrição: ${response.status}`);
      }

      console.log('Subscrição removida do backend com sucesso');
    } catch (error) {
      console.error('Erro ao remover subscrição do backend:', error);
    }
  }

  /**
   * Obtém estatísticas das notificações
   */
  getNotificationStats(): {
    isSupported: boolean;
    isEnabled: boolean;
    permission: NotificationPermission;
    hasServiceWorker: boolean;
  } {
    return {
      isSupported: this.isNotificationSupported(),
      isEnabled: this.isNotificationEnabled(),
      permission: 'Notification' in window ? Notification.permission : 'denied',
      hasServiceWorker: !!this.registration
    };
  }

  /**
   * Envia uma notificação de teste via backend
   */
  async sendTestNotification(): Promise<boolean> {
    try {
      const response = await fetch(`${getApiUrl()}/notifications/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          title: '🎉 Depósito Realizado!',
          body: 'João Silva realizou um depósito e chegou ao estágio 4',
          icon: BRAND_LOGO_192,
          badge: BRAND_LOGO_192,
          data: {
            type: 'deposit_realized',
            leadId: 'test-123',
            leadName: 'João Silva',
            stage: 4,
            url: '/crm'
          },
          actions: [
            {
              action: 'view_lead',
              title: 'Ver Lead'
            },
            {
              action: 'contact',
              title: 'Contatar'
            }
          ]
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Erro ao enviar notificação de teste:', errorData);
        return false;
      }

      const result = await response.json();
      console.log('✅ Notificação de teste enviada:', result);
      return result.success;

    } catch (error) {
      console.error('Erro ao enviar notificação de teste:', error);
      return false;
    }
  }
}

// Instância singleton do serviço
export const notificationService = new NotificationService();

// Tornar disponível globalmente para testes
(window as any).NotificationService = notificationService;

export default notificationService;
